/**
 * Zod schema for the "Register Server" form (F032 MCP Governance) — Doc 69 Layer 2 output,
 * imported by `McpServerRegisterForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs
 * `VS-01`..`VS-12` mirror the verbatim messages previously hand-rolled as manual validators in
 * `mcpServerFormFields.tsx` (kept there for its enum option lists + `mapServerErrorToFormState`
 * history/tests; validation itself now lives here).
 */
import { z } from 'zod';
import type { DataSensitivity, McpAccessLevel, McpServerRegistrationInput } from '../../../types';
import { MCP_ACCESS_LEVEL_OPTIONS, DATA_SENSITIVITY_OPTIONS } from './mcpServerFormFields';

function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

// reason: MCP_ACCESS_LEVEL_OPTIONS/DATA_SENSITIVITY_OPTIONS are runtime-verified non-empty arrays
// of the exported union type; zod's z.enum() requires a literal non-empty tuple type.
const NETWORK_ACCESS_OPTIONS = MCP_ACCESS_LEVEL_OPTIONS as [McpAccessLevel, ...McpAccessLevel[]];
const DATA_SENSITIVITY_TUPLE = DATA_SENSITIVITY_OPTIONS as [DataSensitivity, ...DataSensitivity[]];

export const McpServerRegisterSchema = z.object({
  // VS-01 (required @ submit) / VS-02 (max 200 @ blur)
  serverName: z
    .string()
    .trim()
    .min(1, 'Server name is required')
    .max(200, 'Server name must be 200 characters or fewer'),
  // VS-03 (required) / VS-04 (max 1000) / VS-05 (absolute http/https)
  serverUrl: z
    .string()
    .trim()
    .min(1, 'Server URL is required')
    .max(1000, 'Server URL must be 1000 characters or fewer')
    .refine(isAbsoluteHttpUrl, { message: 'Server URL must be a valid absolute URL' }),
  // VS-06 (optional, max 200)
  ownerId: z
    .string()
    .max(200, 'Owner id must be 200 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // VS-07 (optional, max 200)
  ownerTeam: z
    .string()
    .max(200, 'Owner team must be 200 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // VS-08 (optional, max 100)
  environment: z
    .string()
    .max(100, 'Environment must be 100 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // VS-09 — 'None' is itself a valid McpAccessLevel, so the Dropdown's default selection already
  // satisfies this rule in practice (matches the original manual validator's behaviour).
  networkAccessLevel: z.enum(NETWORK_ACCESS_OPTIONS, { message: 'Select a network access level' }),
  // VS-10
  filesystemAccessLevel: z.enum(NETWORK_ACCESS_OPTIONS, { message: 'Select a filesystem access level' }),
  secretsAccessRequired: z.boolean(),
  // VS-11
  dataAccessLevel: z.enum(DATA_SENSITIVITY_TUPLE, { message: 'Select a data access level' }),
  // VS-12 (optional, valid JSON when non-empty)
  metadataJson: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || isValidJson(v), { message: 'Metadata must be valid JSON' })
    .transform((v) => v || undefined),
});

/** The shape RHF's `useForm<T>`/`defaultValues` are typed against (pre-transform). */
export type McpServerRegisterFormValues = z.input<typeof McpServerRegisterSchema>;
/** The shape `handleSubmit(onValid)` receives (post-transform — trimmed, undefined-if-empty). */
export type McpServerRegisterFormOutput = z.output<typeof McpServerRegisterSchema>;

// Compile-time proof the schema's output assembles a valid A-01 request DTO.
type _AssertAssignable = McpServerRegisterFormOutput extends McpServerRegistrationInput ? true : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
